import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { useSession } from "../../auth/session"
import Api from "../../api/api"
import { meSelector } from "../../redux/globalSlice"
import { Organization, orgRoleLabels, UserRole } from "../../api/models"
import { ImageFieldView, useImageField } from "../ImageField"
import { buildImageSrcSet } from "../widgets/imageSrcSet"
import Spinner from "../widgets/Spinner"
import { backdropDismiss } from "../../utils/backdropDismiss"

// Onboard a vendor (system admin only) — the AddSkuDialog pattern:
// conditionally mounted modal. On success it navigates straight into the
// fresh org's page, where the first vendor admin gets added.
function AddOrganizationDialog({ accessToken, onClose, onCreated }: {
  accessToken: string
  onClose: () => void
  onCreated: (organization: Organization) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [handle, setHandle] = useState("")
  const [name, setName] = useState("")
  const [websiteUrl, setWebsiteUrl] = useState("")
  const [step, setStep] = useState<"form" | "saving" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")
  // the logo presigns against the typed handle — the org doesn't exist yet,
  // so the upload precedes the POST that mints it (system-admin-only path)
  const logo = useImageField(accessToken, (width) =>
    Api.createOrganizationImageUpload({ orgId: handle.trim(), width, accessToken }))

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const busy = step === "saving"
  const formValid = handle.trim() !== "" && name.trim() !== ""
    && (websiteUrl.trim() === "" || websiteUrl.trim().startsWith("https://"))

  const handleSubmit = async () => {
    if (!formValid) return
    setStep("saving")
    setErrorMessage("")
    try {
      let imageWidth: number | undefined
      let imageRev: string | undefined
      if (logo.file !== null) {
        const uploaded = await logo.upload("")
        imageWidth = uploaded.width
        imageRev = uploaded.rev
      }
      const { organization } = await Api.createOrganization({
        id: handle.trim(),
        name: name.trim(),
        websiteUrl: websiteUrl.trim() !== "" ? websiteUrl.trim() : undefined,
        imageWidth, imageRev,
        accessToken
      })
      onCreated(organization)
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Add Organization</h2>

        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Handle</span>
            <input
              type="text"
              className="generate-codes-input"
              value={handle}
              onChange={(e) => setHandle(e.target.value.toLowerCase())}
              disabled={busy}
              placeholder="e.g. newfangled-audio"
              autoFocus
            />
            <span className="generate-codes-hint">
              Permanent — becomes the vendor's upload namespace and URL
              component. Lowercase letters, digits, - and _.
            </span>
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Display name</span>
            <input
              type="text"
              className="generate-codes-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              maxLength={255}
              placeholder="e.g. Newfangled Audio"
            />
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Website URL (optional)</span>
            <input
              type="text"
              className="generate-codes-input"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              disabled={busy}
              maxLength={2048}
              placeholder="https://www.newfangledaudio.com"
            />
            {websiteUrl.trim() !== "" && !websiteUrl.trim().startsWith("https://") && (
              <span className="generate-codes-error">Must start with https://</span>
            )}
          </label>

          <ImageFieldView fields={logo} busy={busy} label="Logo (PNG, optional)" />
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={!formValid || busy}
            onClick={handleSubmit}
          >
            {busy ? <><Spinner size={12} /> Creating…</> : "Create"}
          </button>
        </div>
      </div>
    </dialog>
  )
}

// The organizations INDEX (/organizations): system admins see every vendor
// plus the Add Organization header action; members see their own
// organization(s). Each card navigates to the org's page — the sidebar links
// single-org members straight there and only shows this index when there are
// several to choose from (or the caller is a system admin).
export default function Organizations() {
  const session = useSession()
  const navigate = useNavigate()
  const accessToken = session.accessToken ?? ""
  const me = useSelector(meSelector)?.data
  const isSystemAdmin = me?.role === UserRole.Admin
  const memberships = me?.memberships ?? []

  const [allOrgs, setAllOrgs] = useState<Organization[] | undefined>(undefined)
  const [addOpen, setAddOpen] = useState(false)

  // fetched for everyone (any privileged caller may list orgs): system
  // admins build their cards from it, members join it for the logos their
  // memberships don't carry
  useEffect(() => {
    if (!accessToken) return
    let stale = false
    Api.listOrganizations({ accessToken })
      .then(({ organizations }) => { if (!stale) setAllOrgs(organizations) })
      .catch(() => { if (!stale) setAllOrgs([]) })
    return () => { stale = true }
  }, [accessToken])

  // the cards: every org for system admins, own memberships otherwise (with
  // the caller's role as the card detail)
  const orgsById = new Map((allOrgs ?? []).map(o => [o.id, o]))
  const cards = isSystemAdmin
    ? (allOrgs ?? []).map(o => ({ orgId: o.id, orgName: o.name, detail: "", org: o as Organization | undefined }))
    : memberships.map(m => ({ orgId: m.orgId, orgName: m.orgName, detail: orgRoleLabels[m.orgRole], org: orgsById.get(m.orgId) }))

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Organizations</h1>
        {isSystemAdmin && (
          <button
            className="card-link"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            onClick={() => setAddOpen(true)}
          >
            Add organization
          </button>
        )}
      </div>
      <p style={{ color: "var(--nfa-light-grey)", fontSize: "10pt", marginTop: 0 }}>
        An organization owns its SKUs, products, uploads and beta
        testers. Admins manage the workforce, offerings, codes and
        grants; developers manage listings, versions and betas.
      </p>

      <div style={{ marginTop: 24 }}>
        {isSystemAdmin && allOrgs === undefined ? (
          <p className="empty-state"><Spinner size={16} /> Loading organizations…</p>
        ) : cards.length === 0 ? (
          <p className="empty-state">You don't belong to any organization.</p>
        ) : (
          cards.map((card) => (
            <div
              key={card.orgId}
              className="license-group"
              style={{ cursor: "pointer" }}
              title={card.orgId}
              onClick={() => navigate(`/organizations/${card.orgId}`)}
            >
              <div className="product-group-header">
                <h2 className="row-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {card.org?.imageUrl && (
                    <img
                      src={card.org.imageUrl}
                      srcSet={buildImageSrcSet(card.org.imageUrl, card.org.availableWidths ?? undefined)}
                      sizes="24px"
                      alt=""
                      style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 3 }}
                    />
                  )}
                  {card.orgName}
                </h2>
                {card.detail !== "" && (
                  <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>{card.detail}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {addOpen && (
        <AddOrganizationDialog
          accessToken={accessToken}
          onClose={() => setAddOpen(false)}
          onCreated={(organization) => navigate(`/organizations/${organization.id}`)}
        />
      )}
    </div>
  )
}
